import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

type LogoSize = 'sm' | 'md' | 'lg'

/**
 * - `compact`: logo + wordmark senza payoff "Energia consapevole" —
 *   pensato per navbar e usi dove il payoff sarebbe illeggibile.
 * - `full`: versione storica con payoff sotto il wordmark — per
 *   splash, hero, login pre-auth dove c'è più spazio.
 */
type LogoVariant = 'compact' | 'full'

const SIZES: Record<LogoSize, { width: number; height: number; className: string }> = {
  sm: { width: 130, height: 36, className: 'h-9' },
  md: { width: 190, height: 52, className: 'h-[52px]' },
  lg: { width: 220, height: 60, className: 'h-[60px]' },
}

const SOURCES: Record<LogoVariant, { light: string; dark: string }> = {
  compact: {
    light: '/logo-compact-light.png',
    dark: '/logo-compact-dark.png',
  },
  full: {
    light: '/logo-light.png',
    dark: '/logo-dark.png',
  },
}

/**
 * Logo Enermed. Sceglie automaticamente la variante chiara/scura in base al
 * `data-theme` del root (gestito da ThemeProvider).
 */
export function Logo({
  size = 'md',
  variant = 'compact',
  className,
}: {
  size?: LogoSize
  variant?: LogoVariant
  className?: string
}) {
  const dims = SIZES[size]
  const sources = SOURCES[variant]
  const [src, setSrc] = useState(sources.light)

  useEffect(() => {
    const update = () => {
      const isDark = document.documentElement.dataset.theme === 'dark'
      setSrc(isDark ? sources.dark : sources.light)
    }
    update()
    const obs = new MutationObserver(update)
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => obs.disconnect()
  }, [sources.dark, sources.light])

  return (
    <img
      src={src}
      alt="Enermed"
      width={dims.width}
      height={dims.height}
      className={cn(dims.className, 'w-auto select-none', className)}
      draggable={false}
    />
  )
}
